import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ClipboardList, RefreshCw, AlertTriangle, CheckCircle, Clock, Zap } from 'lucide-react';

const PRIORITY_CONFIG = {
  URGENT: { label: 'Urgent', color: 'bg-red-500/10 text-red-600 border-red-200', icon: AlertTriangle },
  HIGH: { label: 'Ridicat', color: 'bg-amber-500/10 text-amber-600 border-amber-200', icon: Clock },
  MEDIUM: { label: 'Mediu', color: 'bg-blue-500/10 text-blue-600 border-blue-200', icon: CheckCircle },
};

export default function SmartInventoryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterPriority, setFilterPriority] = useState('all');
  const [checkedBins, setCheckedBins] = useState(new Set());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/smart-inventory');
      setData(res.data);
    } catch { toast.error('Eroare la incarcarea datelor'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleChecked = (binId) => {
    setCheckedBins(prev => {
      const next = new Set(prev);
      next.has(binId) ? next.delete(binId) : next.add(binId);
      return next;
    });
  };

  if (loading || !data) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
    </div>
  );

  const filtered = data.suggestions.filter(s =>
    filterPriority === 'all' || s.priority === filterPriority
  );

  const progress = data.suggestions.length > 0
    ? Math.round((checkedBins.size / data.suggestions.length) * 100)
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" data-testid="smart-inv-title">
            <ClipboardList className="w-7 h-7 text-indigo-600" /> Inventar Inteligent
          </h1>
          <p className="text-sm text-slate-500 mt-1">Sugestii prioritizate pentru inventariere - {data.positions_to_check} pozitii de verificat</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-1" /> Actualizeaza</Button>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-xl border p-4 shadow-sm">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600">Progres Inventariere</span>
          <span className="text-sm font-bold text-indigo-600">{checkedBins.size} / {data.suggestions.length} verificate ({progress}%)</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-indigo-500 to-emerald-500 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { key: 'all', label: 'Total', val: data.positions_to_check, cls: 'bg-slate-50 border-slate-200 text-slate-700' },
          { key: 'URGENT', label: 'Urgent', val: data.urgent, cls: 'bg-red-50 border-red-200 text-red-700' },
          { key: 'HIGH', label: 'Ridicat', val: data.high, cls: 'bg-amber-50 border-amber-200 text-amber-700' },
          { key: 'MEDIUM', label: 'Mediu', val: data.medium, cls: 'bg-blue-50 border-blue-200 text-blue-700' },
        ].map(s => (
          <button key={s.key} onClick={() => setFilterPriority(filterPriority === s.key ? 'all' : s.key)}
            className={`p-3 rounded-lg border text-left transition-all ${s.cls} ${filterPriority === s.key ? 'ring-2 ring-indigo-400' : ''}`}
            data-testid={`stat-${s.key}`}>
            <div className="text-2xl font-bold">{s.val}</div>
            <div className="text-xs font-medium mt-0.5">{s.label}</div>
          </button>
        ))}
      </div>

      {/* Suggestions List */}
      <div className="space-y-2">
        {filtered.map((s, idx) => {
          const cfg = PRIORITY_CONFIG[s.priority] || PRIORITY_CONFIG.MEDIUM;
          const Icon = cfg.icon;
          const done = checkedBins.has(s.bin_id);
          return (
            <div key={s.bin_id} className={`bg-white rounded-xl border p-4 shadow-sm transition-all ${done ? 'opacity-50 border-emerald-200 bg-emerald-50/50' : ''}`} data-testid={`suggestion-${s.code}`}>
              <div className="flex items-start gap-4">
                <button onClick={() => toggleChecked(s.bin_id)} className={`mt-1 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all shrink-0 ${done ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 hover:border-indigo-400'}`} data-testid={`check-${s.code}`}>
                  {done && <CheckCircle className="w-4 h-4 text-white" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono font-bold text-slate-800">{s.code}</span>
                    <Badge className={`${cfg.color} border text-[10px] gap-1`}><Icon className="w-3 h-3" /> {cfg.label}</Badge>
                    <span className="text-xs text-slate-400 ml-auto">Scor: {s.priority_score}</span>
                  </div>
                  <div className="text-xs text-slate-500 mb-2">
                    {s.cabinet} &rarr; {s.shelf} &rarr; {s.position} &middot; {s.items_count} articole &middot; {s.total_qty} buc &middot; {s.activity_30d} miscari/luna
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {s.reasons.map((r, j) => (
                      <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 flex items-center gap-1">
                        <Zap className="w-2.5 h-2.5 text-amber-500" /> {r}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="text-center py-12 text-slate-400">Nicio sugestie pentru filtrul selectat</div>
        )}
      </div>
    </div>
  );
}
